import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

// Cognito User Pool configuration
const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);

class CognitoService {
  // Sign up a new user
  signUp(username, password) {
    return new Promise((resolve, reject) => {
      const attributeList = [];
      
      userPool.signUp(username, password, attributeList, null, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  // Confirm sign up with verification code
  confirmSignUp(username, code) {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: username,
        Pool: userPool,
      };
      
      const cognitoUser = new CognitoUser(userData);
      
      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  // Resend confirmation code
  resendConfirmationCode(username) {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: username,
        Pool: userPool,
      };
      
      const cognitoUser = new CognitoUser(userData);
      
      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  // Sign in user
  signIn(username, password) {
    return new Promise((resolve, reject) => {
      const authenticationData = {
        Username: username,
        Password: password,
      };
      
      const authenticationDetails = new AuthenticationDetails(authenticationData);
      
      const userData = {
        Username: username,
        Pool: userPool,
      };
      
      const cognitoUser = new CognitoUser(userData);
      
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }

  // Forgot password - send reset code
  forgotPassword(username) {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: username,
        Pool: userPool,
      };
      
      const cognitoUser = new CognitoUser(userData);
      
      cognitoUser.forgotPassword({
        onSuccess: (result) => {
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }

  // Confirm forgot password with code and new password
  confirmForgotPassword(username, code, newPassword) {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: username,
        Pool: userPool,
      };
      
      const cognitoUser = new CognitoUser(userData);
      
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: (result) => {
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }

  // Sign out current user
  signOut() {
    return new Promise((resolve) => {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
      resolve();
    });
  }

  // Get current authenticated user
  getCurrentUser() {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }
      
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }
        
        if (!session.isValid()) {
          reject(new Error('Session is not valid'));
          return;
        }
        
        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            reject(err);
            return;
          }
          
          const userInfo = {
            username: cognitoUser.getUsername(),
            attributes: attributes.reduce((acc, attr) => {
              acc[attr.getName()] = attr.getValue();
              return acc;
            }, {}),
            session: session,
          };
          
          resolve(userInfo);
        });
      });
    });
  }

  // Check if user is authenticated
  isAuthenticated() {
    return new Promise((resolve) => {
      this.getCurrentUser()
        .then(() => resolve(true))
        .catch(() => resolve(false));
    });
  }
}

export default new CognitoService(); 